import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { CreditCard, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import { billingApi } from '../../../api/client';
import type { BillingInvoice } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

const STATUS_COLORS: Record<string, string> = {
  paid:           'text-win bg-win/10',
  open:           'text-warn bg-warn/10',
  draft:          'text-gray-400 bg-gray-400/10',
  uncollectible:  'text-loss bg-loss/10',
  void:           'text-gray-400 bg-gray-400/10',
};

// ── Main component ─────────────────────────────────────────────────────────────

export function InvoicesSettingsPage() {
  const { can } = usePermissions();
  const canView  = can('billing.view');

  const [invoices,   setInvoices]   = useState<BillingInvoice[]>([]);
  const [isLoading,  setIsLoading]  = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    billingApi.getInvoices()
      .then(setInvoices)
      .catch((err) => setError((err as Error).message ?? 'Failed to load invoices'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view invoices.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Invoices</h2>
        <p className="text-sm text-gray-500 mt-1">
          View and download your billing history.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading invoices…
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-surface-1 border border-surface-4 rounded-lg p-8 text-center">
          <CreditCard className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No invoices yet</p>
          <p className="text-xs text-gray-600 mt-1">Invoices will appear here after your first payment.</p>
        </div>
      ) : (
        <div className="bg-surface-1 border border-surface-4 rounded-lg overflow-hidden">
          <table className="w-full text-sm" aria-label="Invoice history">
            <thead>
              <tr className="border-b border-surface-4 text-left">
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Date</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Period</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Amount</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 sr-only">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-300">{formatDate(inv.createdAt)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {inv.billingPeriodStart && inv.billingPeriodEnd
                      ? `${formatDate(inv.billingPeriodStart)} – ${formatDate(inv.billingPeriodEnd)}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-200 font-medium">
                    {formatCurrency(inv.amountPaid, inv.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        STATUS_COLORS[inv.status] ?? 'text-gray-400 bg-gray-400/10',
                      )}
                    >
                      {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inv.invoicePdf && (
                      <a
                        href={inv.invoicePdf}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-200 transition-colors"
                        aria-label={`Download invoice PDF for ${formatDate(inv.createdAt)}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
